import { AuthService, Season } from '../auth/auth.service';
import { LanguageService } from '../shared/services/language.service';
import { OperationalReportService } from './operational-report.service';

import { DatexPipe } from '../shared/datex.pipe';

import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { RouterLink } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import {
  categoryColorSet,
  categoryLabel,
} from './operational-report.categories';
import { OpCategory, OpReport, OpReportStatus } from './operational-report.model';
import moment, { Moment } from 'moment/moment';
import { startWith } from 'rxjs';

export const MODULES = [
  CommonModule,
  MatFormFieldModule,
  MatDatepickerModule,
  MatSelectModule,
  ReactiveFormsModule,
  MatButtonModule,
  MatCardModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [TranslatePipe, DatexPipe];

@Component({
  selector: 'app-op-report',
  templateUrl: './operational-report.list.component.html',
  styleUrls: ['./operational-report.list.component.scss'],
  providers: [OperationalReportService],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, RouterLink],
})
export class OperationalReportListComponent implements OnInit {
  private auth = inject(AuthService);
  private operationalReportService = inject(OperationalReportService);
  public languageService = inject(LanguageService);

  // Löst eine gespeicherte Kategorie (key) auf ihren Übersetzungs-Key auf
  // (vordefiniert) bzw. gibt den Freitext unverändert zurück (eigene
  // Kategorie) – siehe operational-report.categories.ts.
  readonly categoryLabel = categoryLabel;
  readonly categoryColorSet = categoryColorSet;

  today = moment(moment().format('YYYY-MM-DD 00:00:00'));
  allowedDates = [];
  customerDocument: any;
  dateRange = new FormGroup({
    start: new FormControl<Moment>(null, Validators.required),
    end: new FormControl<Moment>(null, Validators.required),
  });

  private formStatus = toSignal(this.dateRange.statusChanges, {
    initialValue: this.dateRange.status,
  });

  dateRangeInvalid = computed(() => this.formStatus() === 'INVALID');

  private startStatusSig = toSignal(
    this.dateRange.controls.start.statusChanges.pipe(
      startWith(this.dateRange.controls.start.status)
    )
  );

  private endStatusSig = toSignal(
    this.dateRange.controls.end.statusChanges.pipe(
      startWith(this.dateRange.controls.end.status)
    )
  );

  startHasMatError = computed(() => {
    this.startStatusSig();
    return this.dateRange.controls.start.hasError('matStartDateInvalid');
  });

  endHasMatError = computed(() => {
    this.endStatusSig();
    return this.dateRange.controls.end.hasError('matEndDateInvalid');
  });

  startHasMatRequiredError = computed(() => {
    this.startStatusSig();
    return this.dateRange.controls.start.hasError('required');
  });

  endHasMatRequiredError = computed(() => {
    this.endStatusSig();
    return this.dateRange.controls.end.hasError('required');
  });

  dateFilter = (d: Moment | null): boolean => {
    return this.allowedDates.indexOf(d.unix()) >= 0;
  }; // for calendar --> only shows dates in calendar that are in allowedDates

  reports = signal<OpReport[]>([]);

  // Sentinel-Werte für die "Alle"/"Keine"-Einträge im Kategorie-Filter –
  // werden nie selbst als Auswahl zurückgeschrieben (s. onCategoryFilterChange),
  // sondern lösen nur die entsprechende Aktion aus.
  readonly ALL_CATEGORIES_VALUE = '__all__';
  readonly NO_CATEGORIES_VALUE = '__none__';

  // Status- und Kategorie-Filter wirken rein clientseitig auf die bereits
  // per Zeitraum vom Service geladenen reports() – der Mock-Service kennt
  // (wie das echte BFF) nur customerId + Zeitraum als Query-Parameter.
  statusFilter = signal<'all' | OpReportStatus>('all');
  categoryFilter = signal<string[]>([]);

  // Kategorien, die in den aktuell geladenen reports() tatsächlich
  // vorkommen (vordefinierte wie auch eigene Freitext-Kategorien) – bildet
  // die Optionsliste des Kategorie-Filters.
  availableCategories = computed(() => {
    const seen = new Map<string, OpCategory>();
    for (const report of this.reports()) {
      for (const category of report.content.categories) {
        if (!seen.has(category.key)) {
          seen.set(category.key, category);
        }
      }
    }
    return [...seen.values()];
  });

  // Bereinigt categoryFilter() um Keys, die nach einer neuen Zeitraum-Abfrage
  // nicht mehr unter den geladenen reports() vorkommen – verhindert, dass
  // eine "unsichtbare" Auswahl (kein passendes mat-option mehr vorhanden)
  // trotzdem weiterfiltert.
  activeCategoryFilter = computed(() => {
    const availableKeys = new Set(
      this.availableCategories().map(category => category.key)
    );
    return this.categoryFilter().filter(key => availableKeys.has(key));
  });

  // Reagiert auf die "Alle"/"Keine"-Sentinel-Optionen im Kategorie-Filter,
  // statt sie wie normale Kategorien als Auswahl zu übernehmen.
  onCategoryFilterChange(selected: string[]): void {
    if (selected.includes(this.ALL_CATEGORIES_VALUE)) {
      this.categoryFilter.set(
        this.availableCategories().map(category => category.key)
      );
      return;
    }
    if (selected.includes(this.NO_CATEGORIES_VALUE)) {
      this.categoryFilter.set([]);
      return;
    }
    this.categoryFilter.set(selected);
  }

  filteredReports = computed(() => {
    const status = this.statusFilter();
    const categories = this.activeCategoryFilter();
    return this.reports().filter(report => {
      if (status !== 'all' && report.content.status !== status) {
        return false;
      }
      if (
        categories.length &&
        !report.content.categories.some(category =>
          categories.includes(category.key)
        )
      ) {
        return false;
      }
      return true;
    });
  });

  ngOnInit() {
    this.customerDocument = this.auth.getCustomerDocument();
    this.getDatesForCalendar();
    this.initFirstView();
  }

  getDatesForCalendar(): void {
    const dates = [];
    const customerDocument = this.auth.getCustomerDocument();
    // for every product
    for (const product of [
      'eppProducts',
      'egpProducts',
      'egpSummerProducts',
      'egwProducts',
      'egwSummerProducts',
    ]) {
      if (customerDocument.hasOwnProperty(product)) {
        const singleMainProduct = customerDocument[product]; // e.g. eopProducts
        if (this.auth.isProductActive(product, undefined, true)) {
          for (const singleProductType of singleMainProduct) {
            if (singleProductType.active) {
              const startDate = moment(
                moment(singleProductType['contractStartDtg']).format(
                  'YYYY-MM-DD 00:00:00'
                )
              );
              let endDate = this.today;

              // when product has contractExpiryDtg
              if (singleProductType['contractExpiryDtg']) {
                endDate = moment(
                  moment(singleProductType['contractExpiryDtg']).format(
                    'YYYY-MM-DD 00:00:00'
                  )
                );
                if (endDate.isAfter(this.today)) {
                  endDate = this.today;
                }
              }

              // check each day between start and expiry date
              for (
                const dtg = startDate;
                dtg.isBefore(endDate) || dtg.isSame(endDate);
                dtg.add(1, 'day')
              ) {
                const timeStamp = dtg.unix();
                if (dates.indexOf(timeStamp) < 0) {
                  // check season
                  if (this.isDateInSeason(dtg, singleProductType['saison'])) {
                    dates.push(timeStamp);
                  }
                }
              }
            }
          }
        }
      }
      this.allowedDates = dates;
    }
  }
  isDateInSeason(day: Moment, season: Season): boolean {
    if (!season) {
      return true;
    }
    // convert to moment
    const start: Moment = moment({
      year: day.year(),
      month: Number(season.start.month) - 1,
      day: Number(season.start.day),
    });
    const end: Moment = moment({
      year: day.year(),
      month: Number(season.end.month) - 1,
      day: Number(season.end.day),
    });

    if (start.isSameOrBefore(end)) {
      // start <= end, z.B. 01.04.-30.10.
      return !(day.isBefore(start) || day.isAfter(end));
    }
    // start > end, z.B. 01.11.-30.04.
    if (day.isSameOrAfter(start) || day.isSameOrBefore(end)) {
      // z.B. today = 05.12. || today = 24.03
      return true;
    }
    return false;
  }

  onQueryData(
    startDate = this.dateRange.value.start,
    endDate = this.dateRange.value.end
  ): void {
    // NOTE: one day added to dtgEnd to really get pdf till the date picked in calendar,
    //       e.g endDate picked in calendar is 24.01, without adding one day API would only deliver PDF's till 23.01
    // ----------------------------------------------------------------------
    const dtgStart = startDate.format('YYYY-MM-DDT00:00:00');
    // NOTE: 1 days added to really get the pdfs of the chosen day in calendar
    const dtgEnd = endDate.add(1, 'days').format('YYYY-MM-DDT00:00:00');

    this.getReports(dtgStart, dtgEnd);
  }

  initFirstView() {
    const dtgEnd = this.getNewestOrMoment(moment().add(1, 'day')).format(
      'YYYY-MM-DDT00:00:00'
    );
    const dtgStart = moment(dtgEnd)
      .subtract(30, 'days')
      .format('YYYY-MM-DDT00:00:00');
    this.dateRange.controls.start.setValue(moment(dtgStart));
    this.dateRange.controls.end.setValue(moment(dtgEnd));

    // getNewestOrMoment() klemmt dtgEnd auf den spätesten im Kalender
    // wählbaren Tag (abhängig von Vertragslaufzeiten) – das kann "heute
    // 00:00 Uhr" sein, also vor "jetzt". Für die eigentliche Abfrage darf
    // das Fenster nie vor "morgen" enden, sonst fehlen heute neu angelegte
    // Einsatzprotokolle in der Liste.
    const queryEnd = moment
      .max(moment(dtgEnd), moment())
      .add(1, 'day')
      .format('YYYY-MM-DDT00:00:00');
    const queryStart = moment(queryEnd)
      .subtract(30, 'days')
      .format('YYYY-MM-DDT00:00:00');
    this.getReports(queryStart, queryEnd);
  }

  getReports(start: string, end: string) {
    const customerId = this.auth.getCustomerDocument()['customerId'];
    this.operationalReportService
      .getReports(customerId, start, end)
      .subscribe(reports => {
        // Neueste zuerst – report.start ist ein ISO-Datetime-String, der
        // sich auch als reiner String-Vergleich korrekt chronologisch
        // sortieren lässt.
        const sorted = [...reports].sort((a, b) =>
          b.start.localeCompare(a.start)
        );
        this.reports.set(sorted);
      });
  }

  // Bearbeiten ist nur möglich, solange der Einsatz nicht abgeschlossen ist –
  // sonst öffnet der Klick auf eine Zeile den Report nur zum Ansehen.
  editModeFor(report: OpReport): 'edit' | 'view' {
    return report.content.status === 'closed' ? 'view' : 'edit';
  }

  private getNewestOrMoment(value: Moment): Moment {
    if (!this.allowedDates?.length) {
      return value?.clone();
    }

    const maxAllowed = moment(
      Math.max(
        ...this.allowedDates.map(allowedDate =>
          allowedDate < 1e12 ? allowedDate * 1000 : allowedDate
        )
      )
    );

    // if there's any allowed date greater than the moment
    if (value.isAfter(maxAllowed)) {
      return maxAllowed;
    }

    return value;
  }
}
